import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsTechnicianCoordinator } from 'shared/permissions/useIsTechnicianCoordinator';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { loadingActions } from 'store';
import { useAppDispatch } from 'store/store';
import { Option, OptionDescription, StatusOption } from 'shared/types/general';
import toast from 'shared/utils/toast';
import WarehouseForm from '../components/form/WarehouseForm';
import { LocationTab } from '../components/tabs/LocationTab';
import { StockTab } from '../components/tabs/StockTab';
import { ThresholdTab } from '../components/tabs/ThresholdTab';
import { IWarehouse } from '../types';
import { productService, categoryService, contractService } from 'shared/services';
import { labelThresholdService } from '../api/label-threshold.service';
import { productWarehouseService } from '../api/product-warehouse.service';
import { warehouseService } from '../api/warehouse.service';

export function EditWarehouse() {
  const { t } = useTranslation();
  const translationPrefix = 'pages.warehouse.edit';
  const dispatch = useAppDispatch();
  const { warehouseId } = useParams();

  const isDisabledEdit = useIsTechnicianCoordinator() || useIsTechnician();
  const isSuperAdmin = IsSuperAdminGuard();

  const [warehouse, setWarehouse] = useState<IWarehouse>();
  const [categories, setCategories] = useState<OptionDescription[]>([]);
  const [products, setProducts] = useState<Option[]>([]);
  const [labelOptions, setLabelOptions] = useState<OptionDescription[]>([]);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);

  useEffect(() => {
    if (warehouseId) {
      warehouseService.get(+warehouseId).then(setWarehouse);
      productService.getAllBaseInformation().then(setProducts);
      categoryService.getAllBaseInformation().then(setCategories);
      contractService.getAllBaseInformation().then(setContracts);
    }
    productWarehouseService.options().then((res) => {
      setStatusOptions(
        res.actions.POST.status.choices
          .filter((item) => item.display_name !== 'BLOCKED')
          .map((item) => {
            return {
              ...item,
              display_name: t(`entities.product.status_options.${item.display_name}`)
            };
          })
      );
    });
    labelThresholdService.getAllBaseInformation().then(setLabelOptions);
  }, []);

  const updateWarehouse = async (values) => {
    dispatch(loadingActions.startLoading());
    warehouseService.update(warehouse.id, values).then((res) => {
      toast.success('Magazzino modificato con successo');
    });
    dispatch(loadingActions.stopLoading());
  };

  const tabConfig: TabItem[] =
    warehouse && warehouseId
      ? [
          {
            label: t('global.base-info'),
            component: (
              <WarehouseForm
                warehouse={warehouse}
                onSubmit={updateWarehouse}
                disableEdit={isDisabledEdit}
              />
            )
          },
          {
            label: t('menu.thresholds'),
            component: (
              <ThresholdTab
                warehouseId={warehouse.id}
                products={products}
                categories={categories}
                contracts={contracts}
                statusOptions={statusOptions}
                labelOptions={labelOptions}
                translationPrefix={translationPrefix}
              />
            )
          },
          {
            label: t('menu.stock'),
            component: <StockTab warehouseId={warehouse.id} />
          },
          {
            label: t('menu.locations'),
            visible: isSuperAdmin,
            variant: 'admin',
            component: (
              <LocationTab warehouseId={warehouse.id} translationPrefix={translationPrefix} />
            )
          }
        ]
      : [];

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={warehouse?.code}>
      <EnhancedTabs
        tabs={tabConfig}
        variant="scrollable"
        documentConfig={{
          serviceName: 'warehouse',
          entityId: warehouse?.id,
          dictParams: { warehouse: warehouse?.id },
          customFolderName: `menu.warehouses/${warehouse?.code}`,
          folderDescription: '',
          disableEdit: isDisabledEdit
        }}
        historyConfig={{ service: warehouseService, entityId: warehouse?.id }}
      />
    </TenantPage>
  );
}

export default EditWarehouse;
